import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Button, Input, Label, Modal, ModalFooter, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import type { Contact } from "@/lib/types";

interface DupMatch {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  matched_on: string[];
  visits: number;
  last_visit: string | null;
}
interface NameMatch { id: string; name: string; email: string | null; phone: string | null }

/**
 * Intentionally tiny: first name, last name, email, phone. Everything
 * else (address, socials, custom fields, lifecycle, do-not-contact, …)
 * is edited inline on the contact-detail page once the row exists.
 *
 * Keeping the create flow this short means the user can capture a new
 * contact in seconds instead of staring at a dozen fields they don't
 * need to fill in yet.
 */
export function NewContactDialog({
  open, onClose, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (c: Contact) => void;
}) {
  const toast = useToast();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  // "can we have an alert if we are starting a new client if that same
  // phone/email is already in the system" — shown while she types, so the
  // second file is never started, and never a block: two people really do
  // share a mobile number and the desk is the one who can tell.
  const [dupes, setDupes] = useState<DupMatch[]>([]);
  const [nameDupes, setNameDupes] = useState<NameMatch[]>([]);

  useEffect(() => {
    if (open) {
      setFirstName(""); setLastName(""); setEmail(""); setPhone("");
      setDupes([]); setNameDupes([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const e = email.trim(), p = phone.trim(), f = firstName.trim(), l = lastName.trim();
    if (!e && !p && !(f && l)) { setDupes([]); setNameDupes([]); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.get<{ matches: DupMatch[]; name_matches: NameMatch[] }>("/contacts/duplicates", {
        ...(e ? { email: e } : {}), ...(p ? { phone: p } : {}),
        ...(f ? { first_name: f } : {}), ...(l ? { last_name: l } : {}),
      })
        .then((d) => { if (!cancelled) { setDupes(d.matches ?? []); setNameDupes(d.name_matches ?? []); } })
        .catch(() => { if (!cancelled) { setDupes([]); setNameDupes([]); } });
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, email, phone, firstName, lastName]);

  const canSubmit = (firstName.trim() || lastName.trim() || email.trim() || phone.trim());

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Add at least a name, email, or phone");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post<{ contact: Contact }>("/contacts", {
        first_name: firstName.trim() || null,
        last_name:  lastName.trim() || null,
        email:      email.trim() || null,
        phone:      phone.trim() || null,
      });
      toast.success("Contact created");
      onCreated(r.contact);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to create contact");
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="New contact" size="sm">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>First name</Label>
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} autoFocus />
          </div>
          <div>
            <Label>Last name</Label>
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
        </div>
        <div>
          <Label>Email</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" />
        </div>
        <div>
          <Label>Phone</Label>
          <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1…" />
        </div>
        {dupes.length > 0 && (
          <div data-duplicate-warning className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5">
            <p className="flex items-center gap-1.5 text-xs font-medium text-amber-200">
              <AlertTriangle className="h-3.5 w-3.5" />
              {dupes.length === 1 ? "This client is already on file" : "These clients are already on file"}
            </p>
            <ul className="mt-1.5 space-y-1.5">
              {dupes.map((d) => (
                <li key={d.id} className="text-xs">
                  <Link to={`/contacts/${d.id}`} onClick={onClose} className="font-medium text-amber-100 underline underline-offset-2">
                    {d.name}
                  </Link>
                  <span className="text-amber-200/70">
                    {" — same "}{d.matched_on.join(" and ")}
                    {d.visits > 0 ? `, ${d.visits} visit${d.visits === 1 ? "" : "s"} on file` : ", no visits yet"}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-1.5 text-[11px] text-amber-200/70">
              Open their file instead of starting a new one, so their paperwork and history stay together.
              If this really is somebody else on the same number, carry on.
            </p>
          </div>
        )}
        {dupes.length === 0 && nameDupes.length > 0 && (
          <div className="rounded-lg border border-zinc-700 bg-zinc-900/60 px-3 py-2 text-[11px] text-zinc-400">
            Same name, different details:{" "}
            {nameDupes.map((d, i) => (
              <span key={d.id}>
                {i > 0 && ", "}
                <Link to={`/contacts/${d.id}`} onClick={onClose} className="text-zinc-200 underline underline-offset-2">
                  {d.name}
                </Link>
                {d.phone ? ` (${d.phone})` : d.email ? ` (${d.email})` : ""}
              </span>
            ))}
            . Probably a different person - worth a look.
          </div>
        )}
        <p className="text-[11px] text-zinc-500">
          You'll be able to add address, socials, tags, and custom fields right after the contact is created.
        </p>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={saving} disabled={!canSubmit}>Create</Button>
      </ModalFooter>
    </Modal>
  );
}
